'use client'
import { useLocale, useTranslations } from "next-intl"
import styles from "./PlanSelector.module.css"
import { useMemberships } from "@/apiUtilities/queries/useMemberships"
import { Membership } from "@/interfaces/memberships_types"
import { useEffect, useRef, useState } from "react"
import { useCreateCheckout } from "@/apiUtilities/mutations/useCreateCheckout"
import { CreateCheckoutData } from "@/interfaces/api/stripe/api_request_types"
import { toast } from "react-toastify"
import Notification from "@/components/Notification/Notification"
import { useFreeTrial } from "@/apiUtilities/mutations/useFreeTrial"
import { useRouter } from "@/i18n/navigation"
import { API_Error } from "@/interfaces/api/response/api_response_types"
import Plan from "@/components/Plan/Plan"
import { FreeTrialErrorsTypes } from "@/interfaces/api/purchases/api_response_types"
import { useLocalize } from "@/apiUtilities/queries/useLocalize"
import { useModalStore } from "@/stores/useModalStore"
import { ModalTypes } from "@/interfaces/api/generic/api_modals_types"
import DTPaymentModal from "@/components/Modals/DTPaymentModal/DTPaymentModal"
import ProcessingDCBPaymentModal from "../Modals/ProcessingDCBPayment/ProcessingDCBPayment"
import { pushToDataLayer } from "@/utils/helpers"
import { useUserStore } from "@/stores/useUserStore"
import { usePromoCode } from "@/apiUtilities/mutations/usePromoCode"
import IconDiscount from "@/assets/img/Icon_Discount.svg"
import IconSuccess from "@/assets/img/Icon_CircleFilledCheck.svg"
import IconError from "@/assets/img/Icon_CircleFilledCross.svg"
import Icon from "@/components/UI/Icon/Icon"
import { CircularProgress, InputAdornment, Skeleton, TextField } from "@mui/material"
import { useSearchParams } from "next/navigation"
import { normalizePromoCode, PROMO_CODE_SESSION_STORAGE_KEY } from "@/utils/promoCode"

interface PlanSelectorProps {
	presentational?: boolean,
	withButtons?: boolean
}

export default function PlanSelector({ presentational = false, withButtons = false }: PlanSelectorProps) {
	const te = useTranslations('Errors')
	const tc = useTranslations('Codes')
	const locale = useLocale()
	const searchParams = useSearchParams()
	const promoCodeFromUrl = searchParams.get('promocode')
	const { userId } = useUserStore()
	const [code, setCode] = useState<string|undefined>(undefined)
	const previousUserId = useRef(userId)
	const { data, isLoading, isSuccess } = useMemberships(locale, code)
	const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
	const createCheckout = useCreateCheckout(code)
	const freeTrial = useFreeTrial()
	const promoCode = usePromoCode()
	const router = useRouter()
	const localize = useLocalize()
	const { openModal, closeModal } = useModalStore()

	function handlePromoCode() {
		if (presentational) return
		if(!code) return
		promoCode.mutate(code, {
			onSuccess:(response) => {
				toast.success(<Notification text={tc('promoCodeRedeemedSuccessfully')} />)
				router.push('/home')
			},
			onError: (response) => {
				toast.error(<Notification text={tc(`errors.${response.errors[0].string}`)} />)
			}
		})
	}

	function handleChange(e:React.ChangeEvent<HTMLInputElement>) {
		const newCode = e.target.value === '' ? undefined : normalizePromoCode(e.target.value)
		setCode(newCode)
		sessionStorage.removeItem(PROMO_CODE_SESSION_STORAGE_KEY)
	}

	function handleSubscribe(price: string, plan: Membership) {
		if(presentational) return

		// Get the current domain
		const domain = window.location.origin

		// Detect if registration=true is present in the URL params
		const params = new URLSearchParams(window.location.search)
		const hasRegistration = params.get('registration') === 'true'

		const successUrl = hasRegistration ? '/?success=true' : '/?success=true'

		const payload: CreateCheckoutData = {
			price_id: price,
			cancel_url: `${domain}/billing/choose-plan${hasRegistration ? '?registration=true' : ''}`,
			success_url: `${domain}${successUrl}`,
			discount_code: code
		}

		createCheckout.mutate(payload, {
			onSuccess: (response) => {
				if(localize.isSuccess && localize.data.country_code === 'DE') {

					const sentPrice = plan.prices.find(el => el.price_id === price)
					openModal({
						type: ModalTypes.NATIVE,
						content: <DTPaymentModal priceInterval={sentPrice!.interval} onClose={closeModal} billingID={plan.membership_id} checkoutURL={response} redirectURL={window.location.origin + '/billing/choose-plan?processing=true'}/>
					})
				} else {
					window.location.href = response
				}
				pushToDataLayer({
					event: 'plan_chosen',
					user_id: userId,
					plan_id: plan.membership_id,
					price: price,
					free_trial: false
				})
			},
			onError: () => toast.error(<Notification text={te('defaultUnknowError')} />)
		})
	}

	async function handleFreeTrialError (response: API_Error) {
		if(response.code === FreeTrialErrorsTypes.USER_NOT_ELIGIBLE) {
			// toast.error(<Notification text={te('defaultUnknowError')} />)
			router.push('/home')
			return
		}

		toast.error(<Notification text={te('defaultUnknowError')} />)
	}

	async function handleFreeTrialSuccess () {
		pushToDataLayer({
			event: 'plan_chosen',
			user_id: userId,
			free_trial: true
		})
		router.push('/?show_trial=true')
	}

	function handleFreeTrial() {
		if(presentational) return

		freeTrial.mutate(undefined, {
			onSuccess: handleFreeTrialSuccess,
			onError: handleFreeTrialError
		})
	}

	useEffect(() => {
		const loggedOut = previousUserId.current !== null && userId === null
		previousUserId.current = userId

		if (loggedOut) {
			sessionStorage.removeItem(PROMO_CODE_SESSION_STORAGE_KEY)
			setCode(undefined)
			return
		}

		// if (presentational) return

		const promoCode = normalizePromoCode(promoCodeFromUrl ?? '')
			|| sessionStorage.getItem(PROMO_CODE_SESSION_STORAGE_KEY)
		if (promoCode) {
			setCode(promoCode)
			sessionStorage.setItem(PROMO_CODE_SESSION_STORAGE_KEY, promoCode)
		}
	}, [presentational, promoCodeFromUrl, userId])

	useEffect(() => {
		if (!presentational && code?.trim() && isSuccess && !data.code_error) {
			sessionStorage.removeItem(PROMO_CODE_SESSION_STORAGE_KEY)
		}
	}, [code, data, isSuccess, presentational])

	useEffect(() => {
		if(isSuccess) {
			const activePlan = data.memberships.find(plan => plan.active)
			if(activePlan) {
				setSelectedPlan(activePlan.membership_id)
			}
		}
	}, [isSuccess])

	useEffect(() => {
		// Leer los params de la URL y abrir modal si processing=true
		const params = new URLSearchParams(window.location.search)
		if (params.get('processing') === 'true') {
			openModal({
				type: ModalTypes.CENTERED,
				content: <ProcessingDCBPaymentModal onClose={closeModal}/>,
				closeOnBackdrop: false
			})
		}
	}, [])

	function decideCodeAdornment() {
		if(!code) return null
		if(isLoading) return <CircularProgress className={styles.progressAdornment} color="inherit" size="1rem" />
		else if (isSuccess && !data.code_error) return <Icon icon={IconSuccess} className={styles.adornment}/>
		else return <Icon icon={IconError} className={styles.adornment}/>
	}

	return (
		<div className={styles.main}>
			<div className={styles.plans}>
			{isSuccess && data.memberships.map(plan => {
				return <Plan
							key={plan.membership_id}
							handleSubscribe={isSuccess && data.code && !data.code_error ? data.code.type === 'discount' ? handleSubscribe : handlePromoCode : handleSubscribe}
							handleFreeTrial={handleFreeTrial}
							plan={plan}
							selected={plan.membership_id === selectedPlan}
							setSelected={() => setSelectedPlan(plan.membership_id)}
							presentational={presentational}
							withButtons={withButtons}
							active={plan.active}
							discount={!isSuccess || data.code_error ? null : data.code }
						/>
			})}
			{isLoading && <Skeleton className={styles.skeleton} />}
			{isLoading && <Skeleton className={styles.skeleton}/>}
			</div>
			{!presentational && <div className={styles.codeWrapper}>
				<Icon icon={IconDiscount} className={styles.discountIcon}/>
				<TextField label={tc('inputLabel')}
				data-testid="catalogueSearch_input"
				value={code}
				onChange={handleChange}
				className={styles.textfield}
				error={isSuccess && data.code_error}
				helperText={isSuccess ? data.code_error ? tc(`errors.${data.code_error.string}`) : '' : ''}
				slotProps={{
					input: {
						endAdornment:
						<InputAdornment position="end">
							{decideCodeAdornment()}
						</InputAdornment>
					}
				}}
				/>
			</div>}
		</div>
	)
}